export const PRIVACY_POLICY = {
  title: "Privacy Policy",
  effectiveDate: "July 28, 2026",
  lastUpdated: "July 28, 2026",
  intro:
    "North Emerald Holdings LLC (\"we,\" \"us,\" or \"our\") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, and safeguard your information when you interact with our website and services.",
  sections: [
    {
      title: "1. Information We Collect",
      content:
        "We may collect the following types of information when you interact with our website or forms:\n\n- **Contact Information:** Name, email address, mailing address, and phone number\n- **Communication Preferences:** Consent to receive SMS/text messages, email, or phone calls\n- **Form Submission Data:** Any additional information you voluntarily provide through our contact forms, opt-in forms, or inquiries\n- **Technical Data:** IP address, browser type, device information, and website usage data collected automatically through cookies or similar technologies",
    },
    {
      title: "2. How We Use Your Information",
      content:
        "We use the information we collect to:\n\n- Respond to your inquiries and provide requested services\n- Send transactional and/or promotional SMS/text messages, emails, or calls, where you have provided consent\n- Improve our website, products, and services\n- Maintain business records and comply with legal obligations\n- Communicate updates, offers, or account-related information (only where consented to)",
    },
    {
      title: "3. SMS/Text Messaging Communications",
      content:
        "If you opt in to receive text messages from North Emerald Holdings LLC:\n\n- **Message and data rates may apply.**\n- **Message frequency varies** based on your interactions with us.\n- You may **opt out at any time** by replying **STOP** to any message. You may reply **HELP** for assistance.\n- **No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.** Sharing of information with subcontractors performing support services on our behalf (such as customer service) is permitted.\n- **Text messaging originator opt-in data and consent will not be shared with any third parties**, regardless of the use case category, except as required to provide the services requested or as required by law.",
    },
    {
      title: "4. How We Share Your Information",
      content:
        "We do not sell your personal information. We may share your information only in the following limited circumstances:\n\n- With service providers and subcontractors who perform services on our behalf (e.g., customer support, IT hosting), subject to confidentiality obligations\n- When required by law, subpoena, or legal process\n- To protect the rights, property, or safety of North Emerald Holdings LLC, our customers, or others\n- In connection with a business transfer, merger, or acquisition\n\nWe do **not** share SMS opt-in consent or mobile phone numbers with third parties for marketing or promotional purposes.",
    },
    {
      title: "5. Data Retention",
      content:
        "We retain your personal information only as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements.",
    },
    {
      title: "6. Your Rights and Choices",
      content:
        "Depending on your location, you may have the right to:\n\n- Access, correct, or delete your personal information\n- Withdraw consent to receive communications at any time (reply STOP for SMS, or contact us directly)\n- Request a copy of the information we hold about you\n\nTo exercise any of these rights, please contact us using the information below.",
    },
    {
      title: "7. Data Security",
      content:
        "We implement reasonable administrative, technical, and physical safeguards designed to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission or storage is 100% secure.",
    },
    {
      title: "8. Children's Privacy",
      content:
        "Our website and services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children.",
    },
    {
      title: "9. Changes to This Privacy Policy",
      content:
        "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated \"Last Updated\" date.",
    },
    {
      title: "10. Contact Us",
      content:
        "If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:\n\n**North Emerald Holdings LLC**\n119 Pondfield Rd. Unit 424\nBronxville, NY\nsupport@northemeraldholdings.com\n914-613-6622",
    },
  ],
};

export const TERMS = {
  title: "Terms & Conditions",
  effectiveDate: "July 28, 2026",
  lastUpdated: "July 28, 2026",
  intro:
    "Welcome to North Emerald Holdings LLC. These Terms & Conditions govern your access to and use of our website (the \"Site\"). By accessing or using this Site, you agree to be bound by these Terms & Conditions.",
  sections: [
    {
      title: "1. Use of Our Site",
      content:
        "You agree to use this Site only for lawful purposes and in a manner that does not infringe the rights of, restrict, or inhibit anyone else's use of the Site. You may not use this Site to transmit any unlawful, harassing, defamatory, abusive, threatening, or harmful material.",
    },
    {
      title: "2. Services",
      content:
        "North Emerald Holdings LLC provides information about, and access to, our products and/or services as described on this Site. We reserve the right to modify, suspend, or discontinue any part of our services at any time without notice.",
    },
    {
      title: "3. SMS/Text Messaging Terms",
      content:
        "By providing your phone number and opting in through our website, forms, or other communication channels, you agree to the following:\n\n- **Consent:** By submitting your information and opting in, you consent to receive recurring SMS/text messages from North Emerald Holdings LLC at the phone number provided, including messages related to updates, promotions, appointment reminders, and customer service.\n- **Message Frequency:** Message frequency varies.\n- **Message and Data Rates:** Message and data rates may apply for any messages sent to you from us and from you to us.\n- **Opt-Out:** You may opt out of receiving SMS messages at any time by replying **STOP** to any message. After opting out, you will receive one final message confirming your removal. If you wish to opt back in, simply sign up again as you did initially, and we will resume sending messages.\n- **Help:** For assistance, reply **HELP** to any message, or contact us directly at support@northemeraldholdings.com or 914-613-6622.\n- **Carrier Liability:** Carriers are not liable for delayed or undelivered messages.\n- **No Third-Party Sharing:** Text messaging originator opt-in data and consent will not be shared with any third parties for marketing or promotional purposes.\n\nThis SMS program is subject to our [Privacy Policy](/privacy-policy).",
    },
    {
      title: "4. Intellectual Property",
      content:
        "All content on this Site, including text, graphics, logos, and images, is the property of North Emerald Holdings LLC or its licensors and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works from any content on this Site without our prior written consent.",
    },
    {
      title: "5. Disclaimer of Warranties",
      content:
        "This Site and its content are provided \"as is\" and \"as available\" without warranties of any kind, either express or implied. North Emerald Holdings LLC does not warrant that the Site will be uninterrupted, error-free, or free of viruses or other harmful components.",
    },
    {
      title: "6. Limitation of Liability",
      content:
        "To the fullest extent permitted by law, North Emerald Holdings LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of this Site or our services.",
    },
    {
      title: "7. Indemnification",
      content:
        "You agree to indemnify and hold harmless North Emerald Holdings LLC, its members, officers, employees, and agents from any claims, damages, losses, or expenses arising out of your use of the Site or violation of these Terms.",
    },
    {
      title: "8. Governing Law",
      content:
        "These Terms shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law principles.",
    },
    {
      title: "9. Changes to These Terms",
      content:
        "We reserve the right to update or modify these Terms at any time. Changes will be posted on this page with an updated \"Last Updated\" date. Your continued use of the Site after changes are posted constitutes your acceptance of the revised Terms.",
    },
    {
      title: "10. Contact Us",
      content:
        "If you have questions about these Terms & Conditions, please contact us at:\n\n**North Emerald Holdings LLC**\n119 Pondfield Rd. Unit 424\nBronxville, NY\nsupport@northemeraldholdings.com\n914-613-6622",
    },
  ],
};
